import { List } from 'immutable';
import I18n from 'I18n';
import { CONTENT_PLATFORM_PROPERTY_NAME } from 'content-management-data-lib';
import { FilterablePublishStates } from '../constants/filters';
import { AudienceAccessTypes } from 'ui-memberships-settings-lib/constants/AudienceAccessTypes';
import { getContentAccessTypeTableFilterOptions } from 'ui-memberships-settings-lib/utils/contentAccessTypeTag';
const VisibleFilterProperties = [CONTENT_PLATFORM_PROPERTY_NAME.AUDIENCE_ACCESS, CONTENT_PLATFORM_PROPERTY_NAME.APPROVAL_PUBLISH_STATUS, CONTENT_PLATFORM_PROPERTY_NAME.CAMPAIGN, CONTENT_PLATFORM_PROPERTY_NAME.CREATED_BY_USER_ID, CONTENT_PLATFORM_PROPERTY_NAME.CREATED_AT, CONTENT_PLATFORM_PROPERTY_NAME.DOMAIN, CONTENT_PLATFORM_PROPERTY_NAME.LANGUAGE, CONTENT_PLATFORM_PROPERTY_NAME.UPDATED_BY_USER_ID, CONTENT_PLATFORM_PROPERTY_NAME.UPDATED_AT, CONTENT_PLATFORM_PROPERTY_NAME.NAME, CONTENT_PLATFORM_PROPERTY_NAME.STATE, CONTENT_PLATFORM_PROPERTY_NAME.PUBLISH_DATE];
const VisibleColumnProperties = [CONTENT_PLATFORM_PROPERTY_NAME.NAME, CONTENT_PLATFORM_PROPERTY_NAME.STATE, CONTENT_PLATFORM_PROPERTY_NAME.DOMAIN, CONTENT_PLATFORM_PROPERTY_NAME.UPDATED_AT, CONTENT_PLATFORM_PROPERTY_NAME.UPDATED_BY_USER_ID, CONTENT_PLATFORM_PROPERTY_NAME.CREATED_AT, CONTENT_PLATFORM_PROPERTY_NAME.CREATED_BY_USER_ID, CONTENT_PLATFORM_PROPERTY_NAME.PUBLISH_DATE];
const PropertiesWithColumnDisplayLabel = [CONTENT_PLATFORM_PROPERTY_NAME.NAME];
const getPropertyColumnDisplayLabel = propertyName => I18n.text(`content-management-listing-lib.properties.columnDisplayLabels.${propertyName}`);
export const getIsPropertyVisibleAsFilter = (name, propertyDefinitionMap) => {
  // Hide all properties as filters that are not in propertyDefinitionMap as a
  // standard ContentPlatformProperties added by the lib or a custom property
  // specified by the client app in their properties configuration

  if (propertyDefinitionMap[name]) {
    // Check if the propertyDefinitionMap has a property with the given name and if it is visible
    return !propertyDefinitionMap[name].hidden;
  }
  return VisibleFilterProperties.includes(name);
};
export const getIsPropertyVisibleAsColumn = (name, propertyDefinitionMap) => {
  // Hide all properties that are not in propertyDefinitionMap as a
  // standard ContentPlatformProperties added by the lib or a custom property
  // specified by the client app in their properties configuration

  if (propertyDefinitionMap[name]) {
    // Check if the propertyDefinitionMap has a property with the given name and if it is visible
    return !propertyDefinitionMap[name].hidden;
  }
  return VisibleColumnProperties.includes(name);
};
export const transformColumnPropertyOverrideForNewFilterEditor = properties => property => {
  var _properties$name;
  const name = property.name;
  const hidden = !getIsPropertyVisibleAsColumn(name, properties);
  let label = property.label;
  if (!hidden && PropertiesWithColumnDisplayLabel.includes(name)) {
    label = getPropertyColumnDisplayLabel(name);
  }
  const propertyDefinition = (_properties$name = properties[name]) !== null && _properties$name !== void 0 ? _properties$name : {};
  return Object.assign({}, property, propertyDefinition, {
    hidden,
    label,
    displayOrder: -1
  });
};
export const isValueAsList = value => List.isList(value);
export const getAudienceAccessPropertyOptions = () => {
  const audienceAccessTypes = [AudienceAccessTypes.PUBLIC,
  // TODO (memberships FE): Add once CMP BE supports password
  // AudienceAccessTypes.PRIVATE_PASSWORD_REQUIRED,
  AudienceAccessTypes.PRIVATE_REGISTRATION_REQUIRED, AudienceAccessTypes.PRIVATE_SSO_REQUIRED
  // TODO (memberships FE): Add after creating corresponding BE enum
  // AudienceAccessTypes.PRIVATE_ACCESS_GROUP_MEMBERSHIP_REQUIRED,
  ];
  return getContentAccessTypeTableFilterOptions(audienceAccessTypes).map(option => {
    return {
      label: option.text,
      value: option.value,
      hidden: false,
      displayOrder: -1
    };
  });
};
export const getApprovalStatusLangText = val => I18n.text(`productApprovals.ApprovalStatusTag.${val}`);
export const getApprovalStatusPropertyOptions = () => {
  return [{
    label: getApprovalStatusLangText('default'),
    value: 'default',
    hidden: false,
    displayOrder: -1
  }, {
    label: getApprovalStatusLangText('APPROVAL_PENDING'),
    value: 'APPROVAL_PENDING',
    hidden: false,
    displayOrder: -1
  }, {
    label: getApprovalStatusLangText('APPROVED'),
    value: 'APPROVED',
    hidden: false,
    displayOrder: -1
  }];
};
export const getPublishStatusLangText = val => I18n.text(`content-management-listing-lib.filters.publishStatusFilter.${val}`);
export const getContentPublishStatusPropertyOptions = () => [{
  label: getPublishStatusLangText(FilterablePublishStates.ARCHIVED),
  value: FilterablePublishStates.ARCHIVED,
  hidden: false,
  displayOrder: -2
}, {
  label: getPublishStatusLangText(FilterablePublishStates.DRAFT),
  value: FilterablePublishStates.DRAFT,
  hidden: false,
  displayOrder: -1
}, {
  label: getPublishStatusLangText(FilterablePublishStates.PUBLISHED),
  value: FilterablePublishStates.PUBLISHED,
  hidden: false,
  displayOrder: -1
}, {
  label: getPublishStatusLangText(FilterablePublishStates.SCHEDULED),
  value: FilterablePublishStates.SCHEDULED,
  hidden: false,
  displayOrder: -1
}];

// boilerplate required members of Property type
const REQUIRED_PROPERTY_DEFINITION_DEFAULTS = {
  calculated: false,
  deleted: false,
  description: '',
  externalOptions: false,
  externalOptionsReferenceType: undefined,
  fieldType: 'select',
  formField: false,
  groupName: '',
  hasUniqueValue: false,
  isCustomizedDefault: false,
  mutableDefinitionNotDeletable: false,
  readOnlyDefinition: false,
  readOnlyValue: false,
  searchableInGlobalSearch: false,
  displayOrder: -1
};
export const transformFilterPropertyOverrideForNewFilterEditor = (properties, quickFilterPropertyNames) => property => {
  var _properties$name2;
  const name = property.name;
  const hidden = !getIsPropertyVisibleAsFilter(name, properties) || quickFilterPropertyNames.includes(name);
  const propertyDefinition = (_properties$name2 = properties[name]) !== null && _properties$name2 !== void 0 ? _properties$name2 : {};
  return Object.assign({}, REQUIRED_PROPERTY_DEFINITION_DEFAULTS, propertyDefinition, {
    hidden,
    displayOrder: -1
  });
};
export const createPropertyDefinitionFromOverride = propertyOverride => {
  const result = Object.assign({}, REQUIRED_PROPERTY_DEFINITION_DEFAULTS, {
    // members that actually matter for making the filter work
    type: propertyOverride.type,
    name: propertyOverride.name,
    hidden: propertyOverride.hidden,
    label: propertyOverride.label,
    options: propertyOverride.options || [],
    description: propertyOverride.description || '',
    externalOptions: propertyOverride.externalOptions || false,
    externalOptionsReferenceType: propertyOverride.externalOptionsReferenceType,
    referencedObjectType: propertyOverride.referencedObjectType
  });
  return result;
};